import type { CSSProperties } from 'react'
import { useResolvedTheme } from '@/hooks/use-resolved-theme'
import { cn } from '@/lib/utils'

/**
 * A light/dark video that never paints the wrong variant. The resolved theme is `null` on the server
 * and first client render in system mode (the server can't read the OS preference), so we render a
 * same-size neutral placeholder and only swap in the real `<video>` once the painted `dark` class is
 * read on the client — instead of rendering the light variant and flashing to dark after hydration.
 * For an explicit theme cookie the variant is known on the server, so it renders the right video with
 * no placeholder and no flash.
 */
export function ThemedVideo(props: {
  light: string
  dark: string
  posterLight: string
  posterDark: string
  className?: string
  style?: CSSProperties
}) {
  const resolved = useResolvedTheme()

  if (!resolved) {
    return <div aria-hidden className={cn('bg-muted', props.className)} style={props.style} />
  }

  const isDark = resolved === 'dark'
  return (
    <video
      key={resolved}
      autoPlay
      loop
      muted
      playsInline
      className={props.className}
      style={props.style}
      src={isDark ? props.dark : props.light}
      poster={isDark ? props.posterDark : props.posterLight}
    />
  )
}
